import {
  IntranetContentKind,
  PermissionCode,
  intranetFileDownloadPath,
  intranetNewsPath,
  type IntranetFeedItem,
} from '@gb-ticket/shared-types';
import { ArrowLeftIcon, ClipboardCheckIcon, CopyIcon, Loader2Icon, PencilIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/use-page-title';
import { API_BASE_URL, ApiRequestError } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { AckBanner } from '@/products/intranet/components/ack-banner';
import { ColleagueNameButton } from '@/products/intranet/components/colleague-name-button';
import { CommentsSection } from '@/products/intranet/components/comments-section';
import { IntranetBody } from '@/products/intranet/components/intranet-body';
import { IntranetPollView } from '@/products/intranet/components/intranet-poll';
import { IntranetColumns } from '@/products/intranet/components/intranet-columns';
import { IntranetEngagementBar } from '@/products/intranet/components/intranet-engagement-bar';
import { CoverMedia } from '@/products/intranet/components/intranet-feed';
import { coverSrc } from '@/products/intranet/features/intranet/cover-src';
import {
  useIntranetContent,
  useIntranetContents,
} from '@/products/intranet/features/intranet/use-intranet';
import {
  useIntranetPoll,
  useVoteIntranetPoll,
} from '@/products/intranet/features/intranet/use-intranet-poll';
import { useRecordIntranetView } from '@/products/intranet/features/intranet/use-intranet-engagement';
import { useIntranetFiles } from '@/products/intranet/features/intranet/use-intranet-library';
import {
  INTRANET_ROOT,
  intranetAcknowledgementsReportPath,
  intranetEditPath,
} from '@/products/intranet/intranet-paths';

export function NewsDetailPage() {
  const copy = useVoiceCopy();
  const { slug } = useParams();
  const me = useMe();
  const content = useIntranetContent(slug);
  const categoryId = content.data?.category?.id;
  const related = useIntranetContents(
    {
      kind: IntranetContentKind.NEWS,
      categoryId,
      page: 1,
      pageSize: 4,
    },
    Boolean(categoryId),
  );
  const poll = useIntranetPoll(slug);
  const vote = useVoteIntranetPoll(slug ?? '');
  usePageTitle(content.data?.title ?? 'Nachricht');
  const canEdit = hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE);
  const canReadAcks = hasPermission(me.data, PermissionCode.INTRANET_ACKNOWLEDGEMENTS_READ);

  const back = (
    <Button asChild variant="ghost" size="sm" className="-ml-2">
      <Link to={INTRANET_ROOT}>
        <ArrowLeftIcon aria-hidden />
        Alle Nachrichten
      </Link>
    </Button>
  );

  if (content.isPending) {
    return (
      <IntranetColumns>
        <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Nachricht wird geladen …
        </p>
      </IntranetColumns>
    );
  }

  if (content.isError) {
    const missing =
      content.error instanceof ApiRequestError && content.error.error.statusCode === 404;
    return (
      <IntranetColumns>
        <section className="space-y-4">
          {back}
          <p role="alert" className="text-sm text-destructive">
            {missing
              ? copy('intranet.news.not-released')
              : 'Die Nachricht konnte nicht geladen werden.'}
          </p>
        </section>
      </IntranetColumns>
    );
  }

  const item = content.data;
  const cover = coverSrc(item);

  return (
    <IntranetColumns>
      <article className="space-y-4">
        {back}
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <header className="space-y-2 p-4 sm:p-6">
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {item.isDraft ? (
                <Badge variant="outline">Entwurf</Badge>
              ) : (
                formatDate(item.publishedAt)
              )}
              {item.category ? <Badge variant="secondary">{item.category.name}</Badge> : null}
              {item.requiresAcknowledgement ? (
                <Badge variant="outline">Lesebestätigung</Badge>
              ) : null}
              <span>
                von{' '}
                <ColleagueNameButton
                  userId={item.createdBy.id}
                  displayName={item.createdBy.displayName}
                  className="font-medium text-foreground"
                />
              </span>
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <h1 className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
                {item.title}
              </h1>
              {canEdit ? (
                <div className="flex flex-wrap gap-2">
                  {canReadAcks && item.requiresAcknowledgement ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to={intranetAcknowledgementsReportPath(item.slug)}>
                        <ClipboardCheckIcon aria-hidden />
                        Bestätigungen
                      </Link>
                    </Button>
                  ) : null}
                  <Button asChild variant="outline" size="sm">
                    <Link to={intranetEditPath(item.slug)}>
                      <PencilIcon aria-hidden />
                      Bearbeiten
                    </Link>
                  </Button>
                </div>
              ) : null}
            </div>
            <NewsView itemSlug={item.slug} isDraft={item.isDraft} />
            {item.ackDeadline ? (
              <p className="text-sm text-muted-foreground">
                Frist Lesebestätigung: {formatDateTime(item.ackDeadline)}
              </p>
            ) : null}
          </header>

          {cover ? <CoverMedia item={item} src={cover} expandable /> : null}

          <div className="space-y-6 p-4 sm:p-6">
            <AckBanner content={item} />

            <IntranetBody body={item.body} expandImages />
            <NewsAttachments slug={item.slug} body={item.body} />

            {poll.data?.poll ? (
              <IntranetPollView
                poll={poll.data.poll}
                voting={!item.isDraft}
                pending={vote.isPending}
                onVote={(optionId) => {
                  void vote.mutateAsync({ optionId }).catch((error: unknown) => {
                    toast.error(
                      error instanceof ApiRequestError
                        ? error.message
                        : 'Abstimmen fehlgeschlagen.',
                    );
                  });
                }}
              />
            ) : null}

            <IntranetEngagementBar item={item} flush />
          </div>
        </div>

        {item.commentsEnabled === false ? null : (
          <CommentsSection content={item} canEdit={canEdit} />
        )}

        <footer className="space-y-6 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Zuletzt geändert {formatDateTime(item.updatedAt)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => copyArticleLink(item.slug)}
            >
              <CopyIcon aria-hidden />
              Link kopieren
            </Button>
          </div>
          {item.category ? (
            <MoreInCategory
              pending={related.isPending}
              items={(related.data?.items ?? [])
                .filter((entry) => entry.slug !== item.slug)
                .slice(0, 3)}
            />
          ) : null}
        </footer>
      </article>
    </IntranetColumns>
  );
}

/** Files added in the editor that are not already drawn inside the text. */
function NewsAttachments({ slug, body }: { slug: string; body: string | null }) {
  const files = useIntranetFiles(slug);
  const extra = (files.data?.items ?? []).filter((file) => !body?.includes(file.id));
  if (extra.length === 0) {
    return null;
  }
  return (
    <ul className="space-y-1" aria-label="Dateien">
      {extra.map((file) => (
        <li key={file.id}>
          {file.downloadable === false ? (
            <span className="text-sm">{file.filename}</span>
          ) : file.scanStatus === 'CLEAN' || file.scanStatus === 'SKIPPED' ? (
            <a
              href={`${API_BASE_URL}${intranetFileDownloadPath(file.id)}`}
              className="text-sm text-primary underline"
            >
              {file.filename}
            </a>
          ) : file.scanStatus === 'BLOCKED' ? (
            <span className="text-sm text-destructive">{file.filename} wurde abgelehnt.</span>
          ) : (
            <span className="text-sm text-muted-foreground">{file.filename} wird geprüft.</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function copyArticleLink(slug: string): void {
  const path = intranetNewsPath(slug);
  void navigator.clipboard.writeText(`${window.location.origin}${path}`).then(
    () => toast.success('Link kopiert.'),
    () => toast.error('Der Link konnte nicht kopiert werden.'),
  );
}

/** Compact pointers to other articles. Not feed cards — those look like a second post. */
function MoreInCategory({
  items,
  pending,
}: {
  items: readonly IntranetFeedItem[];
  pending: boolean;
}) {
  if (!pending && items.length === 0) {
    return null;
  }
  return (
    <section aria-label="Mehr aus diesem Channel" className="space-y-3 border-t border-border pt-4">
      <h2 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Mehr aus diesem Channel
      </h2>
      {pending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Beiträge werden geladen …
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {items.map((entry) => (
            <li key={entry.id}>
              <RelatedNewsLink item={entry} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RelatedNewsLink({ item }: { item: IntranetFeedItem }) {
  const src = coverSrc(item);
  const to = intranetNewsPath(item.slug);
  const showThumb = Boolean(src) && item.coverMedia !== 'video';
  return (
    <Link
      to={to}
      className="flex gap-3 px-3 py-2.5 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
    >
      <div className="size-14 shrink-0 overflow-hidden rounded-md bg-muted" aria-hidden>
        {showThumb ? <img src={src!} alt="" className="size-full object-cover" /> : null}
      </div>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 text-sm font-medium text-foreground">{item.title}</span>
        {item.publishedAt ? (
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {formatDate(item.publishedAt)}
          </span>
        ) : null}
        {item.teaser ? (
          <span className="mt-0.5 line-clamp-1 block text-sm text-muted-foreground">
            {item.teaser}
          </span>
        ) : null}
      </span>
    </Link>
  );
}

/** Posts one view when a published article has loaded. A repeat the same day does not add a second count. */
function NewsView({ itemSlug, isDraft }: { itemSlug: string; isDraft: boolean }) {
  const { mutate } = useRecordIntranetView(itemSlug);
  const postedFor = useRef<string | null>(null);

  useEffect(() => {
    if (isDraft || postedFor.current === itemSlug) {
      return;
    }
    postedFor.current = itemSlug;
    mutate();
  }, [isDraft, itemSlug, mutate]);

  return null;
}
